import React, { useCallback, useEffect, useState } from 'react';
import { getApiBaseUrl } from '../../config/apiBase';
import { adminFetchInit } from '../../config/adminApi';
import {
  ADMIN_BTN_GHOST,
  ADMIN_BTN_PRIMARY,
  ADMIN_CARD,
  ADMIN_INPUT,
  ADMIN_SECTION_TITLE,
  ADMIN_SELECT,
} from './adminTheme';

function statusTone(status) {
  const n = Number(status || 0);
  if (n >= 500) return 'text-rose-300';
  if (n >= 400) return 'text-amber-300';
  if (n >= 300) return 'text-sky-300';
  if (n >= 200) return 'text-emerald-300';
  return 'text-slate-400';
}

/**
 * Admin Nginx tab — access/error logs with client-side sort controls (server-sorted).
 */
export default function AdminNginxPanel() {
  const base = getApiBaseUrl();
  const [kind, setKind] = useState('access');
  const [sort, setSort] = useState('time');
  const [order, setOrder] = useState('desc');
  const [tail, setTail] = useState(500);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const qs = new URLSearchParams({
        kind,
        sort,
        order,
        tail: String(tail),
      });
      const res = await fetch(`${base}/admin/nginx-logs?${qs}`, adminFetchInit());
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setMeta(json);
      setRows(Array.isArray(json.rows) ? json.rows : []);
      if (json.error) setError(String(json.error));
    } catch (err) {
      setError(String(err?.message || err));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [base, kind, sort, order, tail]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleSort = (field) => {
    if (sort === field) {
      setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort(field);
      setOrder(field === 'time' ? 'desc' : 'asc');
    }
  };

  return (
    <div className="space-y-4">
      <section className={ADMIN_CARD}>
        <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className={ADMIN_SECTION_TITLE}>Nginx reverse-proxy logs</h2>
            <p className="mt-1 max-w-2xl text-xs text-slate-400">
              Access and error lines from the <span className="font-mono text-slate-300">reverse-proxy</span>{' '}
              container. Click column headers to sort.
            </p>
          </div>
          <button type="button" className={ADMIN_BTN_PRIMARY} onClick={load} disabled={loading}>
            {loading ? 'Loading…' : 'Reload'}
          </button>
        </div>

        <div className="mb-4 flex flex-wrap gap-3">
          <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">
            Kind
            <select
              className={ADMIN_SELECT}
              value={kind}
              onChange={(e) => setKind(e.target.value)}
            >
              <option value="access">Access</option>
              <option value="error">Error</option>
              <option value="all">All</option>
            </select>
          </label>
          <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">
            Sort
            <select className={ADMIN_SELECT} value={sort} onChange={(e) => setSort(e.target.value)}>
              <option value="time">Time</option>
              <option value="status">Status</option>
              <option value="path">Path</option>
              <option value="ip">IP</option>
              <option value="size">Size</option>
              <option value="method">Method</option>
            </select>
          </label>
          <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">
            Order
            <select className={ADMIN_SELECT} value={order} onChange={(e) => setOrder(e.target.value)}>
              <option value="desc">Desc</option>
              <option value="asc">Asc</option>
            </select>
          </label>
          <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">
            Tail
            <input
              className={ADMIN_INPUT}
              type="number"
              min={50}
              max={2000}
              value={tail}
              onChange={(e) => setTail(Number(e.target.value) || 500)}
            />
          </label>
          <button type="button" className={`${ADMIN_BTN_GHOST} self-end`} onClick={load}>
            Apply
          </button>
        </div>

        {error ? <p className="mb-3 text-xs font-semibold text-rose-300">{error}</p> : null}
        {meta ? (
          <p className="mb-2 font-mono text-[10px] text-slate-500">
            {meta.raw_count ?? 0} raw · {rows.length} shown · {meta.ts}
          </p>
        ) : null}

        <div className="max-h-[560px] overflow-auto rounded-xl border border-white/10">
          <table className="w-full border-collapse text-left text-xs">
            <thead className="sticky top-0 bg-slate-950">
              <tr className="border-b border-white/10 text-[10px] font-black uppercase tracking-wider text-slate-500">
                {[
                  ['time', 'Time'],
                  ['ip', 'IP'],
                  ['method', 'Method'],
                  ['status', 'Status'],
                  ['path', 'Path'],
                  ['size', 'Size'],
                ].map(([field, label]) => (
                  <th key={field} className="px-3 py-2">
                    <button
                      type="button"
                      className="hover:text-cyan-200"
                      onClick={() => toggleSort(field)}
                    >
                      {label}
                      {sort === field ? (order === 'asc' ? ' ↑' : ' ↓') : ''}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr
                  key={`${row.index ?? i}-${row.raw?.slice(0, 40)}`}
                  className="border-b border-white/5 text-slate-300 hover:bg-white/[0.03]"
                  title={row.raw}
                >
                  <td className="whitespace-nowrap px-3 py-2 font-mono text-[10px] text-slate-400">
                    {row.time || '—'}
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px]">{row.ip || '—'}</td>
                  <td className="px-3 py-2 font-semibold">{row.method || (row.kind === 'error' ? 'ERR' : '—')}</td>
                  <td className={`px-3 py-2 font-mono font-bold tabular-nums ${statusTone(row.status)}`}>
                    {row.status || '—'}
                  </td>
                  <td className="max-w-md truncate px-3 py-2 font-mono text-[11px]" title={row.path || row.raw}>
                    {row.path || row.raw}
                  </td>
                  <td className="px-3 py-2 font-mono tabular-nums text-slate-400">
                    {row.size != null && row.size !== 0 ? row.size : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length === 0 && !loading ? (
            <p className="py-10 text-center text-sm text-slate-500">No matching log lines.</p>
          ) : null}
        </div>
      </section>
    </div>
  );
}
